// Sekce Faktury – seznam se sdíleným toolbar/kartami z shared/list-ui.js.
// Stránkovaný list endpoint (limit/offset) vrací ořezané záznamy vč. customerName
// a serverové summary (KPI přes celý filtrovaný výsledek).
// Detail faktury (úprava draftu, změna stavu, úhrady, e-fakturace) zůstává vlastní.
import { icon } from './icons.js';
import {
  API_BASE_URL, ListView, renderListCard, renderErrorState, renderLoading,
  statusBadge, escapeHtml, formatDate, formatMoney, customerNameOf,
  customerDataText, STATUS_META
} from './shared/list-ui.js';

const INVOICE_STATUS_ORDER = ['draft', 'issued', 'partially_paid', 'paid', 'overdue', 'cancelled'];

const PAYMENT_METHODS = {
  bank: 'Bankovní převod',
  cash: 'Hotově',
  card: 'Platební kartou'
};

// Detailové formátování částek (s desetinami) – seznam používá sdílené formatMoney.
const fmtMoney = (n, currency = 'CZK') => {
  const num = Number(n) || 0;
  return `${num.toLocaleString('cs-CZ', { minimumFractionDigits: num % 1 ? 2 : 0, maximumFractionDigits: 2 })} ${currency}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('cs-CZ');
};

export class InvoicesListManager {
  constructor() {
    this.invoices = [];
    this.summary = null;
    this.view = new ListView({
      key: 'invoices',
      title: 'Faktury',
      subtitle: 'Vystavené faktury a jejich úhrady.',
      searchPlaceholder: 'Hledat (číslo, firma, VS, položka)…',
      statusKind: 'invoice',
      statuses: INVOICE_STATUS_ORDER,
      authorLabels: { me: 'Moje faktury' },
      enableCustomerFilter: true,
      enableKanban: false,
      summarySlot: true,
      onChange: () => this.load()
    });
  }

  // ---------- API (detail) ----------

  async getInvoice(id) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nepodařilo se načíst fakturu');
    const json = await res.json();
    return json.data;
  }

  async createFromOrder(orderId, options = {}) {
    const res = await fetch(`${API_BASE_URL}/invoices/from-order/${orderId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options)
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se vystavit fakturu');
    return json.data;
  }

  async updateInvoice(id, data) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se uložit fakturu');
    return json.data;
  }

  async setStatus(id, status, note = '') {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/status`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se změnit stav faktury');
    return json.data;
  }

  async markPaid(id, paidAmount, paidAt = null) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/mark-paid`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paidAmount, paidAt })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se označit fakturu jako uhrazenou');
    return json.data;
  }

  async deleteInvoice(id) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Nepodařilo se smazat fakturu');
    }
    return true;
  }

  async getAccountingConfig() {
    const res = await fetch(`${API_BASE_URL}/invoices/accounting-config`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nepodařilo se načíst konfiguraci účetnictví');
    const json = await res.json();
    return json.data;
  }

  _downloadBlob(blob, filename) {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }

  async downloadIsdocx(id) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/isdocx`, { credentials: 'include' });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || `HTTP ${res.status}`);
    }
    this._downloadBlob(await res.blob(), `${id}.isdocx`);
  }

  async downloadPdf(id) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/pdf`, { credentials: 'include' });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || `HTTP ${res.status}`);
    }
    this._downloadBlob(await res.blob(), `${id}.pdf`);
  }

  async submitToAccounting(id) {
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/submit-accounting`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = json.data;
      throw err;
    }
    return { data: json.data, externalId: json.externalId };
  }

  // ---------- Seznam ----------

  async showSection() {
    await this.load();
  }

  isDetailView() {
    return Boolean(document.getElementById('invoice-back-btn'));
  }

  async refresh() {
    const container = document.getElementById('faktury-section-content');
    if (!container || this.isDetailView()) return;
    await this.load();
  }

  async load() {
    const container = document.getElementById('faktury-section-content');
    if (!container) return;
    if (!document.getElementById('invoices-list')) {
      this.view.renderShell(container);
      this._bindListEvents();
    } else {
      this.view.renderLoading();
    }

    try {
      const params = this.view.buildParams();
      const response = await fetch(`${API_BASE_URL}/invoices?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Nepodařilo se načíst faktury');
      const result = await response.json();
      this.invoices = result.data || [];
      if (Array.isArray(result.statuses) && result.statuses.length) this.view.statuses = result.statuses;
      if (Array.isArray(result.authors)) this.view.authors = result.authors;
      this.summary = result.summary || null;

      this.renderSummary();
      this.view.renderResults({
        rowsHtml: this.invoices.map((inv) => this.renderInvoiceRow(inv)),
        total: result.total,
        emptyState: {
          title: 'Zatím nemáte žádné faktury',
          hint: 'Faktura vznikne tlačítkem „Vystavit fakturu“ v detailu objednávky se stavem „Fakturovat“.'
        }
      });
    } catch (error) {
      console.error('Chyba při načítání faktur:', error);
      const summaryEl = this.view.summaryContainer;
      if (summaryEl) summaryEl.innerHTML = '';
      this.view.renderCustom(renderErrorState('Nepodařilo se načíst faktury', 'window.invoicesListManager.load()'));
    }
  }

  /** KPI dlaždice ze serverového summary (počítá se přes celý filtrovaný výsledek). */
  renderSummary() {
    const el = this.view.summaryContainer;
    if (!el) return;
    const s = this.summary;
    if (!s || !s.count) {
      el.innerHTML = '';
      return;
    }
    const overdue = Number(s.overdueCount) || 0;
    el.innerHTML = `
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="text-xs uppercase tracking-wide text-gray-500">Celkem fakturováno</div>
          <div class="mt-1 text-xl font-semibold text-gray-900">${fmtMoney(s.totalInvoiced)}</div>
          <div class="text-xs text-gray-400">${s.count} faktur</div>
        </div>
        <div class="bg-white border border-emerald-200 rounded-lg p-4">
          <div class="text-xs uppercase tracking-wide text-emerald-700">Uhrazeno</div>
          <div class="mt-1 text-xl font-semibold text-emerald-700">${fmtMoney(s.totalPaid)}</div>
        </div>
        <div class="bg-white border border-amber-200 rounded-lg p-4">
          <div class="text-xs uppercase tracking-wide text-amber-700">Neuhrazeno</div>
          <div class="mt-1 text-xl font-semibold text-amber-700">${fmtMoney(s.totalUnpaid)}</div>
        </div>
        <div class="bg-white border ${overdue ? 'border-red-200' : 'border-gray-200'} rounded-lg p-4">
          <div class="text-xs uppercase tracking-wide ${overdue ? 'text-red-700' : 'text-gray-500'}">Po splatnosti</div>
          <div class="mt-1 text-xl font-semibold ${overdue ? 'text-red-700' : 'text-gray-900'}">${overdue}</div>
          ${overdue ? `<div class="text-xs text-red-600">${fmtMoney(s.totalOverdue)}</div>` : ''}
        </div>
      </div>
    `;
  }

  renderInvoiceRow(inv) {
    const total = Number(inv.totalPriceWithVat) || 0;
    const paid = Number(inv.paidAmount) || 0;
    const remaining = Math.max(total - paid, 0);
    const currency = inv.currency === 'CZK' ? 'Kč' : (inv.currency || 'Kč');
    const itemNames = (inv.items || []).map((i) => escapeHtml(i.name)).join(', ');

    const badges = [];
    if (inv.isOverdue && inv.status !== 'overdue') {
      badges.push(statusBadge('invoice', 'overdue', 'font-medium'));
    }

    const actions = [
      { action: 'open', icon: 'eye', label: 'Detail faktury', tone: 'primary' },
      { action: 'pdf', icon: 'document-arrow-down', label: 'Stáhnout PDF faktury' }
    ];
    if (inv.status === 'draft') {
      actions.push({ action: 'delete', icon: 'trash', label: 'Smazat rozpracovanou fakturu', tone: 'danger' });
    }

    return renderListCard({
      id: inv.id,
      title: `Faktura <span class="font-semibold">${escapeHtml(inv.id)}</span>`,
      status: { kind: 'invoice', value: inv.status || 'draft' },
      customerName: customerNameOf(inv),
      metaLine: `
        <span class="inline-flex items-center">${icon('calendar', 'w-3.5 h-3.5 mr-1 text-gray-400')}vystaveno ${formatDate(inv.issueDate)}</span>
        <span>·</span><span>splatnost ${formatDate(inv.dueDate)}</span>
        ${inv.variableSymbol ? `<span>·</span><span>VS ${escapeHtml(inv.variableSymbol)}</span>` : ''}
        ${inv.orderId ? `<span>·</span><span>z objednávky #${escapeHtml(inv.orderId)}</span>` : ''}
      `,
      description: itemNames,
      price: formatMoney(total, currency),
      priceNote: remaining > 0 ? `zbývá ${formatMoney(remaining, currency)}` : 'uhrazena',
      badges,
      compact: this.view.compact,
      actions
    });
  }

  _bindListEvents() {
    const list = this.view.listContainer;
    if (!list) return;
    list.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        const { action, id } = actionBtn.dataset;
        if (action === 'open') this.openDetail(id);
        else if (action === 'pdf') this.handleDownloadPdf(id);
        else if (action === 'delete') this.confirmDelete(id);
        return;
      }
      const row = e.target.closest('[data-row-id]');
      if (row && !e.target.closest('button, a')) {
        this.openDetail(row.dataset.rowId);
      }
    });
  }

  async handleDownloadPdf(id) {
    try {
      await this.downloadPdf(id);
      this.notify('PDF faktury staženo', 'success');
    } catch (err) {
      this.notify('Stažení PDF selhalo: ' + err.message, 'error');
    }
  }

  // ---------- Detail ----------

  async openDetail(id) {
    const container = document.getElementById('faktury-section-content');
    if (!container) return;
    container.innerHTML = renderLoading();
    try {
      const invoice = await this.getInvoice(id);
      if (!invoice) throw new Error('Faktura nenalezena');
      this.renderDetail(invoice);
    } catch (err) {
      container.innerHTML = `<div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">${this.escape(err.message)}</div>`;
    }
  }

  renderDetail(inv) {
    const container = document.getElementById('faktury-section-content');
    if (!container) return;

    const total = inv.totalPriceWithVat || 0;
    const remaining = Math.max(total - (inv.paidAmount || 0), 0);
    const isEditable = inv.status === 'draft';

    container.innerHTML = `
      <div class="mb-4">
        <button id="invoice-back-btn" class="inline-flex items-center text-sm text-gray-600 hover:text-blue-600">
          ${icon('chevron-left', 'w-4 h-4 mr-1')}
          Zpět na faktury
        </button>
      </div>

      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Faktura ${this.escape(inv.id)}</h2>
            <div class="mt-1 flex items-center gap-2 flex-wrap">
              ${statusBadge('invoice', inv.status)}
              ${inv.isOverdue && inv.status !== 'overdue' ? statusBadge('invoice', 'overdue', 'font-medium') : ''}
              ${inv.orderId ? `<span class="text-xs text-gray-500">z objednávky ${this.escape(inv.orderId)}</span>` : ''}
              ${inv.offerId ? `<span class="text-xs text-gray-400">· nabídka ${this.escape(inv.offerId)}</span>` : ''}
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            ${this.renderStatusActions(inv)}
          </div>
        </div>

        <form id="invoice-detail-form" class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <fieldset class="space-y-3" ${isEditable ? '' : 'disabled'}>
            <legend class="text-xs uppercase tracking-wide text-gray-500 mb-1">Termíny a platba</legend>
            <div class="grid grid-cols-2 gap-3">
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">Datum vystavení</span>
                <input type="date" name="issueDate" value="${this.escape(inv.issueDate || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </label>
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">DUZP</span>
                <input type="date" name="taxableSupplyDate" value="${this.escape(inv.taxableSupplyDate || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </label>
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">Splatnost</span>
                <input type="date" name="dueDate" value="${this.escape(inv.dueDate || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </label>
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">Způsob platby</span>
                <select name="paymentMethod" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  ${Object.entries(PAYMENT_METHODS).map(([k, v]) => `<option value="${k}" ${k === inv.paymentMethod ? 'selected' : ''}>${v}</option>`).join('')}
                </select>
              </label>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">Variabilní symbol</span>
                <input type="text" name="variableSymbol" value="${this.escape(inv.variableSymbol || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
              </label>
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">Konstantní symbol</span>
                <input type="text" name="constantSymbol" value="${this.escape(inv.constantSymbol || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
              </label>
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">Bankovní účet</span>
                <input type="text" name="bankAccount" value="${this.escape(inv.bankAccount || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
              </label>
              <label class="block">
                <span class="block text-xs text-gray-600 mb-1">IBAN</span>
                <input type="text" name="iban" value="${this.escape(inv.iban || '')}"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
              </label>
            </div>
          </fieldset>

          <fieldset class="space-y-3">
            <legend class="text-xs uppercase tracking-wide text-gray-500 mb-1">Odběratel</legend>
            <pre class="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-800 whitespace-pre-wrap font-sans">${this.escape(this.formatCustomer(inv.customerData) || '— bez zákazníka —')}</pre>

            <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div class="text-xs uppercase tracking-wide text-blue-700 mb-1">Celková částka</div>
              <div class="flex items-baseline justify-between">
                <div>
                  <div class="text-lg font-semibold text-blue-900">${fmtMoney(total, inv.currency)}</div>
                  <div class="text-xs text-blue-700">bez DPH ${fmtMoney(inv.totalPrice, inv.currency)} · DPH ${inv.vatRate}%</div>
                </div>
                <div class="text-right">
                  <div class="text-xs text-gray-600">Uhrazeno</div>
                  <div class="text-base font-semibold ${remaining > 0 ? 'text-amber-700' : 'text-emerald-700'}">${fmtMoney(inv.paidAmount, inv.currency)}</div>
                  ${remaining > 0 ? `<div class="text-xs text-amber-700">Zbývá ${fmtMoney(remaining, inv.currency)}</div>` : ''}
                </div>
              </div>
            </div>
          </fieldset>

          <div class="md:col-span-2 space-y-3">
            <label class="block">
              <span class="block text-xs text-gray-600 mb-1">Poznámka</span>
              <textarea name="note" rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">${this.escape(inv.note || '')}</textarea>
            </label>
          </div>

          ${isEditable ? `
            <div class="md:col-span-2 flex justify-end gap-2">
              <button type="submit" class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                Uložit změny
              </button>
            </div>
          ` : ''}
        </form>

        ${this.renderItemsTable(inv)}
        ${this.renderEinvStatusBlock(inv, this._accountingConfigured)}
        ${this.renderStatusHistory(inv)}
      </div>
    `;

    document.getElementById('invoice-back-btn')?.addEventListener('click', () => this.load());

    // Načti konfiguraci účetnictví (jednou) a případně deaktuj submit tlačítko.
    this._accountingConfigured = null;
    this.getAccountingConfig()
      .then((cfg) => {
        this._accountingConfigured = Boolean(cfg && cfg.configured);
        const btn = document.getElementById('invoice-submit-accounting-btn');
        if (btn && !this._accountingConfigured) {
          btn.disabled = true;
          btn.classList.add('opacity-50', 'cursor-not-allowed');
          btn.title = 'Účetnictví není nakonfigurováno (ACCOUNTING_* env)';
          if (!container.querySelector('[data-einv-warn]')) {
            const warn = document.createElement('div');
            warn.setAttribute('data-einv-warn', '1');
            warn.className = 'text-xs text-amber-700 px-6 pb-2';
            warn.textContent = 'Účetnictví není nakonfigurováno (ACCOUNTING_* env) – odeslání není dostupné.';
            btn.closest('.bg-white.rounded-lg.shadow-md')?.appendChild(warn);
          }
        }
      })
      .catch(() => { /* nezobrazeno – tlačítko zůstává aktivní, chyba se projeví při submitu */ });

    if (isEditable) {
      const form = document.getElementById('invoice-detail-form');
      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        const payload = Object.fromEntries(fd.entries());
        try {
          const updated = await this.updateInvoice(inv.id, payload);
          this.notify('Faktura uložena', 'success');
          this.renderDetail(updated);
        } catch (err) {
          this.notify(err.message, 'error');
        }
      });
    }

    container.querySelectorAll('[data-status-action]').forEach((btn) =>
      btn.addEventListener('click', () => this.handleStatusAction(inv.id, btn.dataset.statusAction))
    );
    container.querySelectorAll('[data-einv-action]').forEach((btn) =>
      btn.addEventListener('click', () => this.handleEinvAction(inv.id, btn.dataset.einvAction))
    );
    document.getElementById('mark-paid-btn')?.addEventListener('click', () => this.openMarkPaidPrompt(inv));
  }

  async handleEinvAction(id, action) {
    if (action === 'isdocx') {
      try {
        await this.downloadIsdocx(id);
        this.notify('ISDOCx stažen', 'success');
      } catch (err) {
        this.notify('Stažení ISDOCx selhalo: ' + err.message, 'error');
      }
      return;
    }
    if (action === 'pdf') {
      await this.handleDownloadPdf(id);
      return;
    }
    if (action === 'submit-accounting') {
      try {
        const { externalId } = await this.submitToAccounting(id);
        this.notify(`Odesláno do účetnictví (ID: ${externalId || '—'})`, 'success');
        const updated = await this.getInvoice(id);
        this.renderDetail(updated);
      } catch (err) {
        if (err.status === 501) {
          this.notify('Účetnictví není nakonfigurováno', 'warning');
        } else if (err.status === 502) {
          this.notify('Chyba odeslání: ' + err.message, 'error');
        } else {
          this.notify('Chyba odeslání: ' + err.message, 'error');
        }
        // Obnov detail, aby se zobrazila zaznamenaná chyba.
        try {
          const updated = await this.getInvoice(id);
          this.renderDetail(updated);
        } catch (_) { /* ignore */ }
      }
      return;
    }
  }

  renderStatusActions(inv) {
    const buttons = [];
    buttons.push(`<button data-einv-action="pdf"
      class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-700 border border-gray-300 bg-white hover:bg-gray-50" title="Stáhnout PDF faktury">
      ${icon('document-arrow-down', 'w-4 h-4 mr-1.5')}
      PDF
    </button>`);
    buttons.push(`<button data-einv-action="isdocx"
      class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-700 border border-gray-300 bg-white hover:bg-gray-50" title="Stáhnout e-fakturu ISDOCx">
      ${icon('arrow-down-tray', 'w-4 h-4 mr-1.5')}
      ISDOCx
    </button>`);
    buttons.push(`<button data-einv-action="submit-accounting" id="invoice-submit-accounting-btn"
      class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
      ${icon('cloud-arrow-up', 'w-4 h-4 mr-1.5')}
      Odeslat do účetnictví
    </button>`);
    if (inv.status === 'draft') {
      buttons.push(`<button data-status-action="issued"
        class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
        Vystavit
      </button>`);
    }
    if (['issued', 'partially_paid', 'overdue'].includes(inv.status) || inv.isOverdue) {
      buttons.push(`<button id="mark-paid-btn"
        class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700">
        Označit jako uhrazeno
      </button>`);
    }
    if (inv.status !== 'cancelled' && inv.status !== 'paid') {
      buttons.push(`<button data-status-action="cancelled"
        class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-red-700 border border-red-300 bg-white hover:bg-red-50">
        Stornovat
      </button>`);
    }
    return buttons.join('');
  }

  renderEinvStatusBlock(inv, accountingConfigured) {
    const parts = [];
    if (inv.accountingExternalId) {
      parts.push(`<div class="text-xs text-emerald-700"><strong>Odesláno do účetnictví:</strong> ${this.escape(inv.accountingExternalId)}${inv.accountingSystem ? ' (' + this.escape(inv.accountingSystem) + ')' : ''}${inv.accountingSubmittedAt ? ' · ' + fmtDate(inv.accountingSubmittedAt) : ''}</div>`);
    }
    if (inv.accountingSubmitError) {
      parts.push(`<div class="text-xs text-red-700"><strong>Chyba odeslání:</strong> ${this.escape(inv.accountingSubmitError)}</div>`);
    }
    if (inv.isdocGeneratedAt) {
      parts.push(`<div class="text-xs text-gray-500">ISDOCx generován: ${fmtDate(inv.isdocGeneratedAt)}</div>`);
    }
    if (!accountingConfigured && !inv.accountingExternalId) {
      parts.push(`<div class="text-xs text-amber-700">Účetnictví není nakonfigurováno (ACCOUNTING_* env) – odeslání není dostupné.</div>`);
    }
    if (!parts.length) return '';
    return `
      <div class="border-t border-gray-200 px-6 py-4">
        <h3 class="text-sm font-semibold text-gray-700 mb-2">E-fakturace</h3>
        <div class="space-y-1">${parts.join('')}</div>
      </div>
    `;
  }

  async handleStatusAction(id, status) {
    let note = '';
    if (status === 'cancelled') {
      note = prompt('Důvod storna (volitelné):') || '';
    }
    try {
      const updated = await this.setStatus(id, status, note);
      this.notify(`Stav změněn: ${STATUS_META.invoice[status]?.label || status}`, 'success');
      this.renderDetail(updated);
    } catch (err) {
      this.notify(err.message, 'error');
    }
  }

  openMarkPaidPrompt(inv) {
    const total = inv.totalPriceWithVat || 0;
    const remaining = Math.max(total - (inv.paidAmount || 0), 0);
    const inputStr = prompt(
      `Částka úhrady (Kč). Zbývá uhradit: ${remaining.toFixed(2)}\nPro plnou úhradu zadejte ${total.toFixed(2)}:`,
      String(total.toFixed(2))
    );
    if (inputStr === null) return;
    const amount = Number(inputStr.replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0) {
      this.notify('Neplatná částka', 'error');
      return;
    }
    this.markPaid(inv.id, amount, null)
      .then((updated) => {
        this.notify('Úhrada zaznamenána', 'success');
        this.renderDetail(updated);
      })
      .catch((err) => this.notify(err.message, 'error'));
  }

  renderItemsTable(inv) {
    const items = inv.items || [];
    if (!items.length) return '';
    return `
      <div class="border-t border-gray-200 px-6 py-4">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Položky faktury</h3>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="text-xs uppercase text-gray-500 border-b border-gray-200">
              <tr>
                <th class="text-left py-2">Popis</th>
                <th class="text-right py-2">Cena</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              ${items.map((item) => `
                <tr>
                  <td class="py-2 text-gray-800">${this.escape(item.name || '—')}</td>
                  <td class="py-2 text-right text-gray-900">${fmtMoney(item.price || 0, inv.currency)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  renderStatusHistory(inv) {
    const history = inv.statusHistory || [];
    if (!history.length) return '';
    return `
      <div class="border-t border-gray-200 px-6 py-4">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Historie stavů</h3>
        <ol class="space-y-2">
          ${history.map((h) => {
            const meta = STATUS_META.invoice[h.status] || { label: h.status, badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400' };
            return `
              <li class="flex items-start gap-2 text-sm">
                <span class="mt-1 inline-block w-1.5 h-1.5 rounded-full ${meta.dot}"></span>
                <div>
                  <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full ${meta.badge}">${this.escape(meta.label)}</span>
                  <span class="ml-2 text-xs text-gray-500">${new Date(h.changedAt).toLocaleString('cs-CZ')} · ${this.escape(h.changedBy || '—')}</span>
                  ${h.note ? `<div class="text-xs text-gray-600 mt-0.5">${this.escape(h.note)}</div>` : ''}
                </div>
              </li>
            `;
          }).join('')}
        </ol>
      </div>
    `;
  }

  async confirmDelete(id) {
    const inv = this.invoices.find((i) => i.id === id);
    if (!inv) return;
    if (!confirm(`Opravdu smazat fakturu ${inv.id}? Tuto akci nelze vrátit.`)) return;
    try {
      await this.deleteInvoice(id);
      this.notify('Faktura smazána', 'success');
      this.load();
    } catch (err) {
      this.notify(err.message, 'error');
    }
  }

  // ---------- Utils ----------

  notify(message, type = 'info') {
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, type);
    } else {
      console.log(`[${type}] ${message}`);
    }
  }

  escape(str) {
    return escapeHtml(str);
  }

  /** Normalizuje customerData (string i objekt z MCP/AI nabídky) na text. */
  formatCustomer(data) {
    return customerDataText(data);
  }
}

InvoicesListManager.STATUS_META = STATUS_META.invoice;
InvoicesListManager.STATUS_ORDER = INVOICE_STATUS_ORDER;

if (typeof window !== 'undefined') {
  window.InvoicesListManager = InvoicesListManager;
}
